import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AmortizationSchedule from "@/components/investor/AmortizationSchedule";
import { usePrintAmortization } from "@/hooks/usePrintAmortization";
import { Investment } from "@/types/investment";
import { Printer, Calculator } from "lucide-react";

export function AmortizationPage() {
  const [inputs, setInputs] = useState({
    currentBalance: "",
    monthlyPayment: "",
    interestRate: "",
    remainingTerms: "",
    propertyAddress: "",
    hasBalloonPayment: false,
    balloonPaymentAmount: "",
    useOriginalDates: false,
    firstPaymentDate: "",
    originalLoanAmount: ""
  });

  const [specialPayments, setSpecialPayments] = useState<Array<{
    month: number;
    type: 'balloon' | 'principal_paydown' | 'payment_change';
    amount: number;
    newPayment?: number;
  }>>([]);

  const [newSpecialPayment, setNewSpecialPayment] = useState<{
    month: string;
    type: 'balloon' | 'principal_paydown' | 'payment_change';
    amount: string;
    newPayment: string;
  }>({
    month: "",
    type: "balloon",
    amount: "",
    newPayment: ""
  });

  const [showSchedule, setShowSchedule] = useState(false);
  const { printAmortizationSchedule } = usePrintAmortization();

  const handleInputChange = (field: string, value: string | boolean) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSchedule = () => {
    // Validate required fields
    if (!inputs.currentBalance || !inputs.monthlyPayment || !inputs.interestRate || !inputs.remainingTerms) {
      alert("Please fill in all required fields");
      return;
    }

    setShowSchedule(true);
  };

  const addSpecialPayment = () => {
    if (!newSpecialPayment.month || !newSpecialPayment.amount) {
      alert("Please fill in month and amount");
      return;
    }

    const month = parseInt(newSpecialPayment.month);
    const amount = parseFloat(newSpecialPayment.amount);
    
    if (month < 1 || month > parseInt(inputs.remainingTerms || "0")) {
      alert(`Month must be between 1 and ${inputs.remainingTerms}`);
      return;
    }

    setSpecialPayments(prev => [...prev, {
      month,
      type: newSpecialPayment.type,
      amount,
      newPayment: newSpecialPayment.type === 'payment_change' ? parseFloat(newSpecialPayment.newPayment || "0") : undefined
    }].sort((a, b) => a.month - b.month));

    setNewSpecialPayment({
      month: "",
      type: "balloon",
      amount: "",
      newPayment: ""
    });
  };

  const removeSpecialPayment = (index: number) => {
    setSpecialPayments(prev => prev.filter((_, i) => i !== index));
  };

  const clearForm = () => {
    setInputs({
      currentBalance: "",
      monthlyPayment: "",
      interestRate: "",
      remainingTerms: "",
      propertyAddress: "",
      hasBalloonPayment: false,
      balloonPaymentAmount: "",
      useOriginalDates: false,
      firstPaymentDate: "",
      originalLoanAmount: ""
    });
    setSpecialPayments([]);
    setShowSchedule(false);
  };

  const handlePrint = () => {
    const investment: Investment = {
      id: "temp-schedule",
      type: "Custom Schedule",
      currentBalance: parseFloat(inputs.currentBalance),
      monthlyPayment: parseFloat(inputs.monthlyPayment),
      interestRate: parseFloat(inputs.interestRate),
      remainingTerms: parseInt(inputs.remainingTerms),
      propertyAddress: inputs.propertyAddress || "Custom Schedule",
      hasBalloonPayment: inputs.hasBalloonPayment,
      balloonPaymentAmount: inputs.hasBalloonPayment ? parseFloat(inputs.balloonPaymentAmount) || 0 : 0,
      askingPrice: parseFloat(inputs.currentBalance),
      status: "active",
      investor_yield: "0%",
      firstPaymentDate: inputs.useOriginalDates ? inputs.firstPaymentDate : undefined,
      originalLoanAmount: inputs.useOriginalDates && inputs.originalLoanAmount ? parseFloat(inputs.originalLoanAmount) : undefined,
      yield: 0,
      location: "Custom",
      riskLevel: "Medium",
      ltv: 0,
      propertyValue: parseFloat(inputs.currentBalance),
      minimumBid: parseFloat(inputs.currentBalance),
      specialPayments: specialPayments.length > 0 ? specialPayments : undefined
    };

    printAmortizationSchedule(investment);
  };

  const mockInvestment: Investment = {
    id: "temp-schedule",
    type: "Custom Schedule",
    currentBalance: parseFloat(inputs.currentBalance) || 0,
    monthlyPayment: parseFloat(inputs.monthlyPayment) || 0,
    interestRate: parseFloat(inputs.interestRate) || 0,
    remainingTerms: parseInt(inputs.remainingTerms) || 0,
    propertyAddress: inputs.propertyAddress || "Custom Schedule",
    hasBalloonPayment: inputs.hasBalloonPayment,
    balloonPaymentAmount: inputs.hasBalloonPayment ? parseFloat(inputs.balloonPaymentAmount) || 0 : 0,
    askingPrice: parseFloat(inputs.currentBalance) || 0,
    status: "active",
    investor_yield: "0%",
    firstPaymentDate: inputs.useOriginalDates ? inputs.firstPaymentDate : undefined,
    originalLoanAmount: inputs.useOriginalDates && inputs.originalLoanAmount ? parseFloat(inputs.originalLoanAmount) : undefined,
    yield: 0,
    location: "Custom",
    riskLevel: "Medium",
    ltv: 0,
    propertyValue: parseFloat(inputs.currentBalance) || 0,
    minimumBid: parseFloat(inputs.currentBalance) || 0,
    specialPayments: specialPayments.length > 0 ? specialPayments : undefined
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Amortization Schedule Generator</h2>
        <p className="text-muted-foreground">Create and print custom amortization schedules</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Loan Details
            </CardTitle>
            <CardDescription>
              Enter the loan information to generate an amortization schedule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentBalance">Current Balance *</Label>
                <Input
                  id="currentBalance"
                  type="number"
                  step="0.01"
                  placeholder="100000"
                  value={inputs.currentBalance}
                  onChange={(e) => handleInputChange("currentBalance", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monthlyPayment">Monthly Payment *</Label>
                <Input
                  id="monthlyPayment"
                  type="number"
                  step="0.01"
                  placeholder="1000"
                  value={inputs.monthlyPayment}
                  onChange={(e) => handleInputChange("monthlyPayment", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interestRate">Interest Rate (%) *</Label>
                <Input
                  id="interestRate"
                  type="text"
                  inputMode="decimal"
                  placeholder="8.5"
                  value={inputs.interestRate}
                  onChange={(e) => handleInputChange("interestRate", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remainingTerms">Remaining Terms (months) *</Label>
                <Input
                  id="remainingTerms"
                  type="number"
                  placeholder="360"
                  value={inputs.remainingTerms}
                  onChange={(e) => handleInputChange("remainingTerms", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="propertyAddress">Property Address (Optional)</Label>
              <Input
                id="propertyAddress"
                placeholder="123 Main St, City, State"
                value={inputs.propertyAddress}
                onChange={(e) => handleInputChange("propertyAddress", e.target.value)}
              />
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Special Payments (Optional)</h3>
              <p className="text-xs text-muted-foreground">Add balloon payments, principal paydowns, or payment changes at specific months</p>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="specialMonth" className="text-xs">Month #</Label>
                  <Input
                    id="specialMonth"
                    type="number"
                    placeholder="12"
                    value={newSpecialPayment.month}
                    onChange={(e) => setNewSpecialPayment(prev => ({ ...prev, month: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="specialType" className="text-xs">Type</Label>
                  <select
                    id="specialType"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newSpecialPayment.type}
                    onChange={(e) => setNewSpecialPayment(prev => ({ ...prev, type: e.target.value as any }))}
                  >
                    <option value="balloon">Balloon Payment</option>
                    <option value="principal_paydown">Principal Paydown</option>
                    <option value="payment_change">Payment Change</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialAmount" className="text-xs">
                  {newSpecialPayment.type === 'payment_change' ? 'Old Payment' : 'Amount'}
                </Label>
                <Input
                  id="specialAmount"
                  type="number"
                  step="0.01"
                  placeholder="5000"
                  value={newSpecialPayment.amount}
                  onChange={(e) => setNewSpecialPayment(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              
              {newSpecialPayment.type === 'payment_change' && (
                <div className="space-y-2">
                  <Label htmlFor="newPayment" className="text-xs">New Payment</Label>
                  <Input
                    id="newPayment"
                    type="number"
                    step="0.01"
                    placeholder="1200"
                    value={newSpecialPayment.newPayment}
                    onChange={(e) => setNewSpecialPayment(prev => ({ ...prev, newPayment: e.target.value }))}
                  />
                </div>
              )}

              <Button onClick={addSpecialPayment} variant="outline" size="sm" className="w-full">
                Add Special Payment
              </Button>

              {specialPayments.length > 0 && (
                <div className="space-y-2 mt-4">
                  <Label className="text-xs">Added Special Payments:</Label>
                  <div className="space-y-1">
                    {specialPayments.map((sp, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-muted p-2 rounded">
                        <span>
                          Month {sp.month}: {sp.type === 'balloon' ? 'Balloon' : sp.type === 'principal_paydown' ? 'Principal Paydown' : 'Payment Change'} - 
                          ${sp.amount.toLocaleString()}
                          {sp.type === 'payment_change' && sp.newPayment && ` → $${sp.newPayment.toLocaleString()}`}
                        </span>
                        <Button onClick={() => removeSpecialPayment(idx)} variant="ghost" size="sm" className="h-6 px-2">
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-4 mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="useOriginalDates"
                  checked={inputs.useOriginalDates}
                  onCheckedChange={(checked) => handleInputChange("useOriginalDates", checked)}
                />
                <Label htmlFor="useOriginalDates" className="text-sm font-semibold">
                  Generate Full Schedule from Original Start Date
                </Label>
              </div>

              {inputs.useOriginalDates && (
                <div className="space-y-3 pl-6 bg-muted/30 p-4 rounded-md">
                  <p className="text-xs text-muted-foreground">
                    Create a complete amortization schedule from the original loan start date with accurate historical payment dates.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="firstPaymentDate" className="text-sm">First Payment Date *</Label>
                      <Input
                        id="firstPaymentDate"
                        type="date"
                        value={inputs.firstPaymentDate}
                        onChange={(e) => handleInputChange("firstPaymentDate", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="originalLoanAmount" className="text-sm">Original Loan Amount</Label>
                      <Input
                        id="originalLoanAmount"
                        type="number"
                        step="0.01"
                        placeholder="Enter original loan amount"
                        value={inputs.originalLoanAmount}
                        onChange={(e) => handleInputChange("originalLoanAmount", e.target.value)}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave blank to use current balance for full schedule
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={generateSchedule} className="flex-1">
                Generate Schedule
              </Button>
              <Button variant="outline" onClick={clearForm}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {showSchedule && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Schedule Preview</CardTitle>
                <Button onClick={handlePrint} variant="outline" size="sm" className="flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                <AmortizationSchedule investment={mockInvestment} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}